import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { transformFromAlignedBox, Point3d, useGetLookAtCuboidTarget } from '@fabric-web/3d-viewer';
import * as globalState from '@/state';
import * as state from '@/components/Analysis/state';
import useAssemblyDetails from '@/hooks/useAssemblyDetails';
import { alignedBoxFromTransform } from '@fabric-web/3d-viewer';

export const useFocusOnAssembly = () => {
  const lastDeeplinkUrlParameters = useAtomValue(state.lastDeeplinkUrlParameters);

  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const cameraType = useAtomValue(globalState.cameraType);
  const cameraDirection = useAtomValue(globalState.cameraDirection);

  const viewerCurrentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const viewerNextCurrentSpherical = useAtomValue(state.viewerNextCurrentSpherical);

  const focalPointsByAssemblyId = useAtomValue(state.focalPointsByAssemblyId);
  const [cameraTarget, setCameraTarget] = useAtom(state.cameraTarget);

  // const camera = useThree((state) => state.camera);

  useAssemblyDetails();

  const previousFocalPoint = useRef<
    | {
        selectedAssemblyId: string | undefined;
        focalPointEntry:
          | {
              focalPoint?: Point3d;
              cameraPose?: {
                position: Point3d;
                lookAt: Point3d;
              };
            }
          | undefined;
      }
    | undefined
  >(undefined);

  const getLookAtCuboidTarget = useGetLookAtCuboidTarget();

  /**
   * Sets camera target to look at the entire selected assembly in 3d view mode
   */
  useEffect(() => {
    if (!lastDeeplinkUrlParameters) return;
    if (
      lastDeeplinkUrlParameters.lookAt &&
      performance.now() < lastDeeplinkUrlParameters.time + 5000
    ) {
      return;
    }

    const focalPointEntry = selectedAssemblyName
      ? focalPointsByAssemblyId[selectedAssemblyName]
      : undefined;

    const oldState = previousFocalPoint.current;
    previousFocalPoint.current = {
      selectedAssemblyId,
      focalPointEntry,
    };

    // Not changing camera position, when exiting spherical view mode
    if (
      oldState?.selectedAssemblyId === selectedAssemblyId &&
      oldState?.focalPointEntry === focalPointEntry
    )
      return;

    if (!selectedAssemblyName) return;
    if (!selectedAssemblyId) return;
    if (cameraTarget) return;
    if (viewerCurrentSpherical || viewerNextCurrentSpherical) return;

    if (!focalPointEntry) return;
    const { focalPoint, cameraPose, box } = focalPointEntry;

    if (box && cameraDirection) {
      const transform = transformFromAlignedBox({
        min: [box.min.x, box.min.y, box.min.z],
        max: [box.max.x, box.max.y, box.max.z],
      });
      const alignedBox = alignedBoxFromTransform(transform);

      const target = getLookAtCuboidTarget(alignedBox, cameraDirection);
      setCameraTarget(target);
    } else if (cameraPose) {
      const { position, lookAt } = cameraPose;
      const { x, y, z } = position;
      const { x: lx, y: ly, z: lz } = lookAt;
      setCameraTarget({
        position: [x, y, z],
        lookAt: [lx, ly, lz],
      });
    } else if (focalPoint) {
      const { x, y, z } = focalPoint;
      setCameraTarget({
        position: [x, y + 15, z + 5],
        lookAt: [x, y, z],
      });
    }
  }, [
    lastDeeplinkUrlParameters,
    cameraTarget,
    focalPointsByAssemblyId,
    selectedAssemblyId,
    selectedAssemblyName,
    viewerCurrentSpherical,
    viewerNextCurrentSpherical,
    setCameraTarget,
    cameraDirection,
    getLookAtCuboidTarget,
    cameraType,
  ]);
};
